import React, { useEffect, useState } from "react";
import { ExternalLink, ShieldCheck, MessageCircle, Loader2, CreditCard, Receipt, CheckCircle2, AlertCircle } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

export default function AgentSettings() {
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const [waNumber, setWaNumber] = useState(user?.whatsapp_number || "");
  const [iban, setIban] = useState(user?.iban || "");
  const [piva, setPiva] = useState(user?.partita_iva || "");
  const [savingWa, setSavingWa] = useState(false);
  const [savingIban, setSavingIban] = useState(false);
  const [savingPiva, setSavingPiva] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);

  const refreshStripeStatus = async () => {
    if (!user?.stripe_account_id && !user?.partita_iva) return;
    try {
      const r = await api.get("/agent/stripe-connect/status");
      setStripeStatus(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    refreshStripeStatus();
    // After Stripe redirect with ?stripe_connect=done refresh status + show toast
    if (params.get("stripe_connect") === "done") {
      toast.success("Onboarding Stripe completato. Verifico lo stato…");
      refresh?.();
      // remove the query param
      params.delete("stripe_connect");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) return null;

  const saveWa = async () => {
    setSavingWa(true);
    try {
      await api.put("/agent/settings", { whatsapp_number: waNumber.trim() });
      await refresh?.();
      toast.success(waNumber.trim() ? "Numero WhatsApp aggiornato" : "Numero WhatsApp rimosso");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSavingWa(false);
    }
  };

  const saveIban = async () => {
    setSavingIban(true);
    try {
      await api.put("/agent/settings", { iban: iban.trim() });
      await refresh?.();
      toast.success(iban.trim() ? "IBAN salvato" : "IBAN rimosso");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSavingIban(false);
    }
  };

  const savePiva = async () => {
    setSavingPiva(true);
    try {
      await api.put("/agent/settings", { partita_iva: piva.trim() });
      await refresh?.();
      toast.success(piva.trim() ? "Partita IVA salvata" : "Partita IVA rimossa");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSavingPiva(false);
    }
  };

  const startStripeConnect = async () => {
    if (!piva.trim()) {
      toast.error("Inserisci e salva prima la Partita IVA");
      return;
    }
    setStripeLoading(true);
    try {
      const r = await api.post("/agent/stripe-connect/onboard");
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile avviare Stripe Connect");
      setStripeLoading(false);
    }
  };

  const stripeConfigured = !!user.stripe_account_id;
  const payoutsReady = stripeStatus?.payouts_enabled || user.stripe_payouts_enabled;

  return (
    <div className="space-y-6 fade-slide max-w-2xl">
      <h1 className="text-3xl font-bold text-[#0A0A0A]">Impostazioni account</h1>
      <div className="vg-card p-6 space-y-3 text-sm">
        <Row label="Nome" value={`${user.nome} ${user.cognome}`} />
        <Row label="Email" value={user.email} />
        <Row label="Telefono" value={user.telefono || "—"} />
        <Row label="Agenzia" value={user.nome_agenzia || "—"} />
        <Row label="Ruolo" value={user.ruolo} />
        <Row label="Subscription" value={user.subscription_status} />
        <Row label="Referral code" value={user.referral_code} />
      </div>

      <div className="vg-card p-6">
        <h3 className="font-bold text-[#0A0A0A] mb-1 flex items-center gap-2">
          <MessageCircle size={18} className="text-[#25D366]" /> Numero WhatsApp per i tuoi lead
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Se compilato, questo numero verrà usato nei link WhatsApp delle email inviate ai lead che ti arrivano tramite referral. Se vuoto, viene usato il numero di telefono inserito al signup.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="tel"
            className="vg-input flex-1"
            placeholder="+39 333 1234567"
            value={waNumber}
            onChange={(e) => setWaNumber(e.target.value)}
            data-testid="settings-whatsapp-input"
          />
          <button
            className="vg-btn-primary px-6"
            onClick={saveWa}
            disabled={savingWa}
            data-testid="settings-whatsapp-save"
          >
            {savingWa ? <Loader2 className="animate-spin" size={16} /> : "Salva"}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">Formato consigliato: prefisso internazionale + numero (es. +39 333 1234567).</p>
      </div>

      <div className="vg-card p-6">
        <h3 className="font-bold text-[#0A0A0A] mb-1 flex items-center gap-2">
          <Receipt size={18} className="text-[#B40101]" /> Pagamenti automatici
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Configura Stripe Connect per ricevere le fee referral direttamente sul tuo conto a fine mese.
        </p>

        <div className="block">
          <span className="block text-sm font-semibold text-[#0A0A0A] mb-1">Partita IVA</span>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              className="vg-input flex-1 font-mono uppercase"
              placeholder="IT12345678901"
              value={piva}
              onChange={(e) => setPiva(e.target.value)}
              data-testid="settings-piva-input"
            />
            <button
              className="vg-btn-secondary px-6"
              onClick={savePiva}
              disabled={savingPiva}
              data-testid="settings-piva-save"
            >
              {savingPiva ? <Loader2 className="animate-spin" size={16} /> : "Salva P.IVA"}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2">Inserendo la tua P.IVA dichiari di essere responsabile della corretta emissione delle fatture a ValutaGenio per le fee ricevute.</p>
        </div>

        <div className="mt-5 pt-5 border-t border-gray-100">
          {!piva.trim() ? (
            <div className="flex items-start gap-2 p-3 rounded-[12px] bg-amber-50 border border-amber-200">
              <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-[#0A0A0A]">
                Per ricevere pagamenti automatici è necessaria una Partita IVA. Contatta il tuo commercialista per aprirla.
              </p>
            </div>
          ) : payoutsReady ? (
            <div className="flex items-center gap-2 p-3 rounded-[12px] bg-green-50 border border-green-200" data-testid="stripe-connect-active">
              <CheckCircle2 size={20} className="text-green-600 shrink-0" />
              <div>
                <p className="text-sm font-bold text-[#0A0A0A]">Pagamenti automatici attivi</p>
                <p className="text-xs text-gray-500">Le fee maturate ti vengono accreditate il 1° di ogni mese.</p>
              </div>
            </div>
          ) : stripeConfigured ? (
            <div className="flex items-start gap-2 p-3 rounded-[12px] bg-amber-50 border border-amber-200">
              <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-bold text-[#0A0A0A]">Onboarding incompleto</p>
                <p className="text-xs text-gray-500 mb-2">Devi completare i dati su Stripe per ricevere i pagamenti.</p>
                <button
                  className="vg-btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm"
                  onClick={startStripeConnect}
                  disabled={stripeLoading}
                  data-testid="stripe-connect-resume"
                >
                  {stripeLoading ? <Loader2 className="animate-spin" size={14} /> : "Completa onboarding Stripe"}
                </button>
              </div>
            </div>
          ) : (
            <button
              className="vg-btn-primary inline-flex items-center gap-2 px-6"
              onClick={startStripeConnect}
              disabled={stripeLoading}
              data-testid="stripe-connect-start"
            >
              {stripeLoading ? <Loader2 className="animate-spin" size={16} /> : "Configura pagamenti automatici"}
            </button>
          )}
        </div>
      </div>

      <div className="vg-card p-6">
        <h3 className="font-bold text-[#0A0A0A] mb-1 flex items-center gap-2">
          <CreditCard size={18} className="text-[#B40101]" /> IBAN per ricevere i pagamenti
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Coordinate bancarie su cui ricevere le fee referral maturate.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            className="vg-input flex-1 font-mono uppercase"
            placeholder="IT60X0542811101000000123456"
            value={iban}
            onChange={(e) => setIban(e.target.value)}
            data-testid="settings-iban-input"
          />
          <button
            className="vg-btn-primary px-6"
            onClick={saveIban}
            disabled={savingIban}
            data-testid="settings-iban-save"
          >
            {savingIban ? <Loader2 className="animate-spin" size={16} /> : "Salva"}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">Le fee maturate vengono liquidate manualmente ogni mese al raggiungimento di una soglia minima di 20€.</p>
      </div>

      <div className="vg-card p-6">
        <h3 className="font-bold text-[#0A0A0A] mb-3 flex items-center gap-2"><ShieldCheck size={18} className="text-[#B40101]" /> Codice di condotta</h3>
        <p className="text-sm text-gray-500 mb-3">Le regole che ogni Property Advisor ValutaGenio rispetta nel suo lavoro quotidiano.</p>
        <a
          href="https://valutagenio.com/policy"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-[#B40101] font-semibold text-sm hover:underline"
          data-testid="settings-codice-condotta"
        >
          Leggi il Codice di condotta ValutaGenio
          <ExternalLink size={14} />
        </a>
      </div>

      <div className="vg-card p-6">
        <h3 className="font-bold text-[#0A0A0A] mb-2">Supporto</h3>
        <p className="text-sm text-gray-500">Per assistenza scrivi a <a href="mailto:paul.k.fasciano@gmail.com" className="text-[#B40101] font-semibold">paul.k.fasciano@gmail.com</a></p>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-gray-100 last:border-0">
      <span className="text-gray-400 font-medium">{label}</span>
      <span className="text-[#0A0A0A] font-semibold">{value}</span>
    </div>
  );
}
